"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus,
  Trash2,
  Globe,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  Copy,
  Star,
  ExternalLink,
  ShieldCheck,
  FileText,
  Link2,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { ActionButton } from "@/components/common/DataViewTypes";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchDomainsRequest,
  createDomainRequest,
  updateDomainRequest,
  deleteDomainRequest,
  resetFormState,
} from "@/store/slices/domain.slice";
import { Domain } from "@/types/models/domain";
import { ConfirmationDialog } from "@/components/modals/ConfirmationDialog";
import { toast } from "sonner";

interface AddDomainForm {
  domain_name: string;
  validation_method: "txt" | "http";
}

const DomainsTab = () => {
  const dispatch = useDispatch<AppDispatch>();

  const domainsState = useSelector((state: RootState) => state.domains);
  const {
    items: domains = [],
    loaded = false,
    loading = false,
    error = null,
    pagination = { page: 1, totalPages: 1, pageSize: 10, total: 0 },
    formState = { loading: false, error: null, success: false },
  } = domainsState || {};
  const [pageIndex, setPageIndex] = useState(0);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Selected domain states
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);

  // Form state
  const [addForm, setAddForm] = useState<AddDomainForm>({
    domain_name: "",
    validation_method: "txt",
  });

  // Validation error
  const [validationError, setValidationError] = useState<string>("");

  // Clipboard state
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Polling state
  const [pollingDomains, setPollingDomains] = useState<Set<string>>(new Set());

  const fetchDomains = useCallback(() => {
    dispatch(fetchDomainsRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  // Fetch domains on mount / pagination with guarded fetches
  useEffect(() => {
    if (loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && domains.length === 0) {
      fetchDomains();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchDomains();
    }
  }, [loaded, domains.length, loading, error?.status, pagination?.page, pageIndex, fetchDomains]);

  // Reset form state when modal closes
  useEffect(() => {
    if (!isAddModalOpen) {
      dispatch(resetFormState());
      setValidationError("");
    }
  }, [isAddModalOpen, dispatch]);

  // Close modal on success
  useEffect(() => {
    if (formState.success && !formState.loading) {
      resetForm();
      setIsAddModalOpen(false);
      toast.success("Domain added successfully!");
    }
  }, [formState.success, formState.loading]);

  // Polling for validation status
  useEffect(() => {
    if (pollingDomains.size === 0) return;

    const interval = setInterval(() => {
      dispatch(fetchDomainsRequest({ page: pageIndex + 1 }));
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [pollingDomains, dispatch, pageIndex]);

  // Auto-start polling for pending domains
  useEffect(() => {
    const pendingDomains = domains.filter(
      (d) => d.validation_status === "pending"
    );
    if (pendingDomains.length > 0) {
      setPollingDomains(new Set(pendingDomains.map((d) => d.id)));
    }
  }, [domains]);

  // Validate subdomain
  const validateSubdomain = (domain: string): boolean => {
    if (!domain) {
      setValidationError("Domain name is required");
      return false;
    }

    // Remove protocol if present
    const cleanDomain = domain.replace(/^https?:\/\//, "").replace(/\/$/, "");

    // Check if it's a valid domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-_.]*\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(cleanDomain)) {
      setValidationError("Please enter a valid domain name");
      return false;
    }

    // Count dots to ensure it's a subdomain
    const parts = cleanDomain.split(".");
    if (parts.length < 3) {
      setValidationError(
        "Only subdomains are allowed. Example: www.yourdomain.com or shop.yourdomain.com"
      );
      return false;
    }

    // Check for valid subdomain (not starting with a dot)
    if (cleanDomain.startsWith(".")) {
      setValidationError("Domain cannot start with a dot");
      return false;
    }

    setValidationError("");
    return true;
  };

  const handleAddDomain = () => {
    if (!validateSubdomain(addForm.domain_name)) {
      return;
    }

    dispatch(createDomainRequest(addForm));
  };

  const resetForm = () => {
    setAddForm({
      domain_name: "",
      validation_method: "txt",
    });
    setValidationError("");
  };

  const handleRemoveDomain = (domain: Domain) => {
    setDomainToDelete(domain);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (domainToDelete) {
      dispatch(deleteDomainRequest({ id: domainToDelete.id }));
      setIsDeleteModalOpen(false);
      setDomainToDelete(null);
      toast.success("Domain deleted successfully!");
    }
  };

  const handleViewValidation = (domain: Domain) => {
    setSelectedDomain(domain);
    setIsValidationModalOpen(true);
  };


  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleRefreshStatus = useCallback(() => {
    fetchDomains();
    toast.success("Domain status refreshed!");
  }, [fetchDomains]);

  const getStatusBadge = (status: Domain["validation_status"] | string) => {
    const statusConfig: Record<
      string,
      { icon: React.ReactNode; color: string; label: string }
    > = {
      active: {
        icon: <CheckCircle2 className="h-3 w-3" />,
        color:
          "bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20",
        label: "Active",
      },
      pending: {
        icon: <Clock className="h-3 w-3 animate-pulse" />,
        color:
          "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-500/20",
        label: "Pending",
      },
      failed: {
        icon: <AlertCircle className="h-3 w-3" />,
        color:
          "bg-red-500/10 text-red-700 dark:text-red-400 hover:bg-red-500/20",
        label: "Failed",
      },
    };

    const config = statusConfig[status] || statusConfig["pending"];

    return (
      <Badge className={`${config.color} inline-flex items-center gap-1.5 font-medium w-fit`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const columns: ColumnDef<Domain>[] = [
    {
      accessorKey: "domain_name",
      header: "Domain",
      size: 400,
      cell: ({ row }) => {
        const domain = row.original;
        return (
          <div className="flex items-center gap-3 min-w-[350px]">
            <div className="h-10 w-10 bg-gradient-to-br from-primary/20 to-primary/5 rounded-lg flex items-center justify-center border border-primary/20">
              <Globe className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-foreground">
                  {domain.domain_name}
                </p>
                {domain.is_primary && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-400"
                  >
                    <Star className="h-3 w-3 text-blue-500 fill-blue-500 mr-1" />
                    Primary
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Added {new Date(domain.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "validation_status",
      header: "Validation",
      cell: ({ row }) => {
        return getStatusBadge(row.original.validation_status);
      },
    },
    {
      accessorKey: "ssl_status",
      header: "SSL",
      cell: ({ row }) => {
        const sslStatus = row.original.ssl_status;
        if (!sslStatus) {
          return <div className="text-sm text-muted-foreground">-</div>;
        }
        return getStatusBadge(sslStatus);
      },
    },
    {
      id: "validation_method",
      header: "Actions",
      size: 140,
      cell: ({ row }) => {
        const domain = row.original;
        if (domain.validation_status === "active" && domain.verified_at) {
          return (
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm text-muted-foreground">
                {new Date(domain.verified_at).toLocaleDateString()}
              </span>
            </div>
          );
        }
        if (
          domain.validation_status === "pending" &&
          domain.validation
        ) {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewValidation(domain)}
              className="text-primary hover:text-primary/80"
            >
              {domain.validation_method === "txt" ? (
                <FileText className="h-4 w-4 mr-1" />
              ) : (
                <Link2 className="h-4 w-4 mr-1" />
              )}
              View Instructions
            </Button>
          );
        }
        if (domain.validation_status === "failed") {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewValidation(domain)}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              <AlertCircle className="h-4 w-4 mr-1" />
              Retry
            </Button>
          );
        }
        return <div className="text-sm text-muted-foreground">-</div>;
      },
    },
    {
      id: "actions",
      header: "Delete",
      size: 100,
      cell: ({ row }) => {
        const domain = row.original;
        const isPrimary = domain.is_primary;

        return (
          <div className="flex items-center justify-center">
            {!isPrimary ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/20"
                onClick={() => handleRemoveDomain(domain)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : (
              <div className="text-xs text-muted-foreground italic">
                Protected
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />,
      onClick: handleRefreshStatus,
      variant: "outline" as any,
      disabled: loading,
    },
    {
      label: "Add Domain",
      icon: <Plus className="h-3 w-3" />,
      onClick: () => setIsAddModalOpen(true),
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-[16px] p-[18px] border border-border/40">
          <DataView
            title="Custom Subdomains"
            description="Connect your custom subdomain and verify ownership to make your store accessible on your own domain"
            columns={columns}
            data={domains}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            loading={loading}
            emptyMessage="No custom subdomains yet. Add your first subdomain to get started."
            actionButtons={actionButtons}
            enableColumnResizing={true}
          />
        </div>
      </TableContainer>

      {/* Add Domain Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[550px] bg-card border border-border/40 shadow-2xl">
          <DialogHeader className="pb-4 border-b border-border/60">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
                <Plus className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Add Custom Subdomain
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Connect your subdomain and choose a validation method
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="grid gap-5 py-6">
            {(formState.error || validationError) && (
              <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {validationError || formState.error?.message || "Failed to add domain"}
                  </p>
                </div>
              </div>
            )}

            <StandardFormInput
              label="Subdomain Name"
              placeholder="e.g., www.yourdomain.com or shop.yourdomain.com"
              value={addForm.domain_name}
              onChange={(e) => {
                setAddForm((prev) => ({ ...prev, domain_name: e.target.value }));
                setValidationError("");
              }}
            />

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">
                Validation Method
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setAddForm((prev) => ({ ...prev, validation_method: "txt" }))
                  }
                  className={`p-4 rounded-lg border-2 transition-all ${
                    addForm.validation_method === "txt"
                      ? "border-primary bg-primary/5"
                      : "border-border/40 hover:border-border"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <FileText
                      className={`h-6 w-6 ${
                        addForm.validation_method === "txt"
                          ? "text-primary"
                          : "text-muted-foreground"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        addForm.validation_method === "txt"
                          ? "text-primary"
                          : "text-foreground"
                      }`}
                    >
                      DNS TXT Record
                    </span>
                    <span className="text-xs text-muted-foreground text-center">
                      Verify via DNS
                    </span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setAddForm((prev) => ({ ...prev, validation_method: "http" }))
                  }
                  className={`p-4 rounded-lg border-2 transition-all ${
                    addForm.validation_method === "http"
                      ? "border-primary bg-primary/5"
                      : "border-border/40 hover:border-border"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Link2
                      className={`h-6 w-6 ${
                        addForm.validation_method === "http"
                          ? "text-primary"
                          : "text-muted-foreground"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        addForm.validation_method === "http"
                          ? "text-primary"
                          : "text-foreground"
                      }`}
                    >
                      HTTP File
                    </span>
                    <span className="text-xs text-muted-foreground text-center">
                      Upload file to server
                    </span>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-400">
                    Important Notes:
                  </p>
                  <ul className="text-sm text-blue-700 dark:text-blue-500 list-disc list-inside space-y-1">
                    <li>Only subdomains are allowed (e.g., www.yourdomain.com)</li>
                    <li>Root domains (e.g., yourdomain.com) cannot be added</li>
                    <li>A primary domain is auto-assigned by the system</li>
                    <li>After adding, follow the validation instructions</li>
                    <li>Validation typically completes within a few minutes to 48 hours</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t border-border/60">
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsAddModalOpen(false);
              }}
              className="border-border/40"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleAddDomain}
              disabled={
                !addForm.domain_name ||
                formState.loading
              }
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
            >
              <Plus className="h-4 w-4 mr-2" />
              {formState.loading ? "Adding..." : "Add Subdomain"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Instructions Modal */}
      <Dialog
        open={isValidationModalOpen}
        onOpenChange={setIsValidationModalOpen}
      >
        <DialogContent className="sm:max-w-[650px] bg-card border border-border/40 shadow-2xl">
          <DialogHeader className="pb-4 border-b border-border/60">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold text-foreground">
                  Domain Validation
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Verify ownership of {selectedDomain?.domain_name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-5">
            {selectedDomain && selectedDomain.validation && (
              <>
                {/* Status Banners */}
                <div className="space-y-3">
                  {/* Domain Validation Status */}
                  <div
                    className={`px-4 py-2.5 rounded-lg border ${
                      selectedDomain.validation_status === "pending"
                        ? "bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800"
                        : selectedDomain.validation_status === "failed"
                        ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                        : "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {selectedDomain.validation_status === "pending" && (
                        <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 animate-pulse" />
                      )}
                      {selectedDomain.validation_status === "failed" && (
                        <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                      )}
                      {selectedDomain.validation_status === "active" && (
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-xs font-semibold mb-0.5">Domain Validation</p>
                        <p
                          className={`text-xs font-medium ${
                            selectedDomain.validation_status === "pending"
                              ? "text-yellow-800 dark:text-yellow-400"
                              : selectedDomain.validation_status === "failed"
                              ? "text-red-800 dark:text-red-400"
                              : "text-green-800 dark:text-green-400"
                          }`}
                        >
                          {selectedDomain.validation_status === "pending" &&
                            "Waiting for validation - Complete the steps below"}
                          {selectedDomain.validation_status === "failed" &&
                            "Validation failed - Check your configuration"}
                          {selectedDomain.validation_status === "active" &&
                            "Domain verified and active"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* SSL Status */}
                  {selectedDomain.ssl_status && (
                    <div
                      className={`px-4 py-2.5 rounded-lg border ${
                        selectedDomain.ssl_status === "pending"
                          ? "bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800"
                          : selectedDomain.ssl_status === "failed"
                          ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                          : "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        {selectedDomain.ssl_status === "pending" && (
                          <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 animate-pulse" />
                        )}
                        {selectedDomain.ssl_status === "failed" && (
                          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                        )}
                        {selectedDomain.ssl_status === "active" && (
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="text-xs font-semibold mb-0.5">SSL Certificate</p>
                          <p
                            className={`text-xs font-medium ${
                              selectedDomain.ssl_status === "pending"
                                ? "text-yellow-800 dark:text-yellow-400"
                                : selectedDomain.ssl_status === "failed"
                                ? "text-red-800 dark:text-red-400"
                                : "text-green-800 dark:text-green-400"
                            }`}
                          >
                            {selectedDomain.ssl_status === "pending" &&
                              "SSL certificate generation in progress - This may take up to 24-48 hours"}
                            {selectedDomain.ssl_status === "failed" &&
                              "SSL certificate generation failed - Please check SSL validation records"}
                            {selectedDomain.ssl_status === "active" &&
                              "SSL certificate active and valid"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* CNAME Record (Required) */}
                {selectedDomain.validation.cname_record && (
                  <div className="border rounded-lg overflow-hidden bg-secondary-background">
                    <div className="px-4 py-2.5 bg-background border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">
                          CNAME Record
                        </span>
                        <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20 text-[10px] px-2 py-0.5">
                          Required
                        </Badge>
                      </div>
                    </div>
                    <div className="px-4 py-3 space-y-2.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground w-24 font-medium">Type:</span>
                        <div className="flex items-center gap-2 flex-1">
                          <code className="text-xs bg-background px-2.5 py-1 rounded font-mono border border-border/50">
                            CNAME
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => copyToClipboard("CNAME", "cname-type")}
                          >
                            {copiedField === "cname-type" ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground w-24 font-medium">Name:</span>
                        <div className="flex items-center gap-2 flex-1">
                          <code className="text-xs bg-background px-2.5 py-1 rounded font-mono truncate max-w-[320px] border border-border/50">
                            {selectedDomain.validation.cname_record.name}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() =>
                              copyToClipboard(
                                selectedDomain.validation?.cname_record?.name || "",
                                "cname-name"
                              )
                            }
                          >
                            {copiedField === "cname-name" ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground w-24 font-medium">Target:</span>
                        <div className="flex items-center gap-2 flex-1">
                          <code className="text-xs bg-background px-2.5 py-1 rounded font-mono border border-border/50">
                            {selectedDomain.validation.cname_record.target}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() =>
                              copyToClipboard(
                                selectedDomain.validation?.cname_record?.target || "",
                                "cname-target"
                              )
                            }
                          >
                            {copiedField === "cname-target" ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-3">
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <div className="flex gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700 dark:text-amber-500">
                            <strong>Note:</strong> If you are using Cloudflare, don't turn on the proxy for the domain
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation Methods (Choose One) */}
                {(selectedDomain.validation.txt_record || selectedDomain.validation.http_file) && (
                  <div className="border rounded-lg overflow-hidden bg-secondary-background">
                    <div className="px-4 py-2.5 bg-background border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">
                          Ownership Validation
                        </span>
                        <Badge className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20 text-[10px] px-2 py-0.5">
                          Choose One
                        </Badge>
                      </div>
                    </div>

                    <Accordion type="single" collapsible className="w-full">
                      {/* TXT Record Option */}
                      {selectedDomain.validation.txt_record && (
                        <AccordionItem value="txt" className="border-b-0">
                          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-background/60">
                            <div className="flex items-center gap-2.5">
                              <FileText className="h-4 w-4" />
                              <span className="text-sm font-medium">DNS TXT Record</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-2.5 pt-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground w-24 font-medium">Type:</span>
                                <div className="flex items-center gap-2 flex-1">
                                  <code className="text-xs bg-background px-2.5 py-1 rounded font-mono border border-border/50">
                                    TXT
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => copyToClipboard("TXT", "txt-type")}
                                  >
                                    {copiedField === "txt-type" ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground w-24 font-medium">Name:</span>
                                <div className="flex items-center gap-2 flex-1">
                                  <code className="text-xs bg-background px-2.5 py-1 rounded font-mono truncate max-w-[320px] border border-border/50">
                                    {selectedDomain.validation.txt_record.name}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() =>
                                      copyToClipboard(
                                        selectedDomain.validation?.txt_record?.name || "",
                                        "txt-name"
                                      )
                                    }
                                  >
                                    {copiedField === "txt-name" ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-start justify-between text-sm">
                                <span className="text-muted-foreground w-24 font-medium pt-1">Value:</span>
                                <div className="flex items-center gap-2 flex-1">
                                  <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all border border-border/50">
                                    {selectedDomain.validation.txt_record.value}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 flex-shrink-0"
                                    onClick={() =>
                                      copyToClipboard(
                                        selectedDomain.validation?.txt_record?.value || "",
                                        "txt-value"
                                      )
                                    }
                                  >
                                    {copiedField === "txt-value" ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      )}

                      {/* HTTP File Option */}
                      {selectedDomain.validation.http_file && (
                        <AccordionItem value="http" className="border-b-0">
                          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-background/60">
                            <div className="flex items-center gap-2.5">
                              <Globe className="h-4 w-4" />
                              <span className="text-sm font-medium">HTTP File Upload</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-2.5 pt-1">
                              <div className="flex items-start justify-between text-sm">
                                <span className="text-muted-foreground w-24 font-medium pt-1">URL:</span>
                                <div className="flex items-center gap-2 flex-1">
                                  <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all flex-1 border border-border/50">
                                    {selectedDomain.validation.http_file.url}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 flex-shrink-0"
                                    onClick={() =>
                                      copyToClipboard(
                                        selectedDomain.validation?.http_file?.url || "",
                                        "http-url"
                                      )
                                    }
                                  >
                                    {copiedField === "http-url" ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                  <a
                                    href={selectedDomain.validation.http_file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-shrink-0"
                                  >
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                      <ExternalLink className="h-3.5 w-3.5" />
                                    </Button>
                                  </a>
                                </div>
                              </div>
                              <div className="flex items-start justify-between text-sm">
                                <span className="text-muted-foreground w-24 font-medium pt-1">Content:</span>
                                <div className="flex items-center gap-2 flex-1">
                                  <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all border border-border/50">
                                    {selectedDomain.validation.http_file.body}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 flex-shrink-0"
                                    onClick={() =>
                                      copyToClipboard(
                                        selectedDomain.validation?.http_file?.body || "",
                                        "http-body"
                                      )
                                    }
                                  >
                                    {copiedField === "http-body" ? (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      )}
                    </Accordion>
                  </div>
                )}

                {/* SSL Validation Records */}
                {selectedDomain.validation.ssl_validation_records && 
                 selectedDomain.validation.ssl_validation_records.length > 0 && (
                  <div className="border rounded-lg overflow-hidden bg-secondary-background">
                    <div className="px-4 py-2.5 bg-background border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">
                          SSL Validation Records
                        </span>
                        <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20 text-[10px] px-2 py-0.5">
                          {selectedDomain.validation.ssl_validation_records.length} Record{selectedDomain.validation.ssl_validation_records.length > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>

                    <Accordion type="single" collapsible className="w-full">
                      {selectedDomain.validation.ssl_validation_records.map((record, index) => {
                        // Check which validation methods are available for this record
                        const hasTxt = record.txt_name && record.txt_value;
                        const hasHttp = record.http_url && record.http_body;
                        
                        return (
                          <AccordionItem key={index} value={`ssl-${index}`} className="border-b-0">
                            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-background/60">
                              <div className="flex items-center justify-between w-full pr-2">
                                <div className="flex items-center gap-2.5">
                                  {hasTxt && hasHttp ? (
                                    <ShieldCheck className="h-4 w-4" />
                                  ) : hasTxt ? (
                                    <FileText className="h-4 w-4" />
                                  ) : (
                                    <Globe className="h-4 w-4" />
                                  )}
                                  <span className="text-sm font-medium">
                                    {hasTxt && hasHttp ? "DNS TXT / HTTP" : hasTxt ? "DNS TXT" : "HTTP File"} - Record {index + 1}
                                  </span>
                                </div>
                                <Badge className={`${
                                  record.status === "validated" 
                                    ? "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20"
                                    : record.status === "failed"
                                    ? "bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20"
                                    : "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border border-yellow-500/20"
                                } text-[10px] px-2 py-0.5 mr-2`}>
                                  {record.status}
                                </Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-3 pt-1">
                                {/* TXT Record Section */}
                                {hasTxt && (
                                  <div className="space-y-2.5">
                                    {hasTxt && hasHttp && (
                                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pb-1 border-b border-border/50">
                                        DNS TXT Record Option
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-muted-foreground w-24 font-medium">Type:</span>
                                      <div className="flex items-center gap-2 flex-1">
                                        <code className="text-xs bg-background px-2.5 py-1 rounded font-mono border border-border/50">
                                          TXT
                                        </code>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0"
                                          onClick={() => copyToClipboard("TXT", `ssl-txt-type-${index}`)}
                                        >
                                          {copiedField === `ssl-txt-type-${index}` ? (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="flex items-start justify-between text-sm">
                                      <span className="text-muted-foreground w-24 font-medium pt-1">Name:</span>
                                      <div className="flex items-center gap-2 flex-1">
                                        <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all border border-border/50">
                                          {record.txt_name}
                                        </code>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 flex-shrink-0"
                                          onClick={() => copyToClipboard(record.txt_name, `ssl-txt-name-${index}`)}
                                        >
                                          {copiedField === `ssl-txt-name-${index}` ? (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="flex items-start justify-between text-sm">
                                      <span className="text-muted-foreground w-24 font-medium pt-1">Value:</span>
                                      <div className="flex items-center gap-2 flex-1">
                                        <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all border border-border/50">
                                          {record.txt_value}
                                        </code>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 flex-shrink-0"
                                          onClick={() => copyToClipboard(record.txt_value, `ssl-txt-value-${index}`)}
                                        >
                                          {copiedField === `ssl-txt-value-${index}` ? (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Separator if both methods available */}
                                {hasTxt && hasHttp && (
                                  <div className="relative py-2">
                                    <div className="absolute inset-0 flex items-center">
                                      <div className="w-full border-t border-border/50"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                      <span className="bg-secondary-background px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                        OR
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* HTTP File Section */}
                                {hasHttp && (
                                  <div className="space-y-2.5">
                                    {hasTxt && hasHttp && (
                                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pb-1 border-b border-border/50">
                                        HTTP File Option
                                      </div>
                                    )}
                                    <div className="flex items-start justify-between text-sm">
                                      <span className="text-muted-foreground w-24 font-medium pt-1">URL:</span>
                                      <div className="flex items-center gap-2 flex-1">
                                        <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all flex-1 border border-border/50">
                                          {record.http_url}
                                        </code>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 flex-shrink-0"
                                          onClick={() => copyToClipboard(record.http_url, `ssl-http-url-${index}`)}
                                        >
                                          {copiedField === `ssl-http-url-${index}` ? (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" />
                                          )}
                                        </Button>
                                        <a
                                          href={record.http_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex-shrink-0"
                                        >
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                            <ExternalLink className="h-3.5 w-3.5" />
                                          </Button>
                                        </a>
                                      </div>
                                    </div>
                                    <div className="flex items-start justify-between text-sm">
                                      <span className="text-muted-foreground w-24 font-medium pt-1">Content:</span>
                                      <div className="flex items-center gap-2 flex-1">
                                        <code className="text-xs bg-background px-2.5 py-1 rounded font-mono break-all border border-border/50">
                                          {record.http_body}
                                        </code>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 flex-shrink-0"
                                          onClick={() => copyToClipboard(record.http_body, `ssl-http-body-${index}`)}
                                        >
                                          {copiedField === `ssl-http-body-${index}` ? (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                          ) : (
                                            <Copy className="h-3.5 w-3.5" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Email addresses if available */}
                                {record.emails && record.emails.length > 0 && (
                                  <div className="flex items-start justify-between text-sm mt-3 pt-3 border-t border-border/50">
                                    <span className="text-muted-foreground w-24 font-medium pt-1">Emails:</span>
                                    <div className="flex-1">
                                      {record.emails.map((email, emailIndex) => (
                                        <div key={emailIndex} className="flex items-center gap-2 mb-1">
                                          <code className="text-xs bg-background px-2.5 py-1 rounded font-mono border border-border/50">
                                            {email}
                                          </code>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0"
                                            onClick={() => copyToClipboard(email, `ssl-email-${index}-${emailIndex}`)}
                                          >
                                            {copiedField === `ssl-email-${index}-${emailIndex}` ? (
                                              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                            ) : (
                                              <Copy className="h-3.5 w-3.5" />
                                            )}
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="pt-4 border-t border-border/60">
            <Button
              variant="outline"
              onClick={() => setIsValidationModalOpen(false)}
              className="border-border/40"
            >
              Close
            </Button>
            <Button
              onClick={handleRefreshStatus}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Check Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDomainToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Domain"
        description={`Are you sure you want to delete ${domainToDelete?.domain_name}? This action cannot be undone and will remove all DNS configurations associated with this domain.`}
        confirmText="Delete Domain"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
};

export default DomainsTab;
